import React, { useEffect, useMemo, useState } from "react";
import user from "../../../src/assets/user.png";
import cross from "../../assets/cross.svg";
import gender from "../../assets/gender.svg";
import imgupload from "../../assets/imgupload.svg";
import addpatient from "../../assets/addpatient.svg";
import help from "../../assets/help.svg";
import sidevector from "../../assets/sidevector.svg";
import patientname from "../../assets/patientname.svg";
import emailid from "../../assets/emailid.svg";
import contact from "../../assets/contact.svg";
import patientage from "../../assets/patientage.svg";
import labnumber from "../../assets/labnumber.svg";
import place from "../../assets/place.svg";
import patientid from "../../assets/patientid.svg";
import { useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";
import useEmailValidation from "../../Hooks/useEmailValidation";
import { translations } from "../../translation/translate";
import AddPatientConfirmationModal from "../../Components/addPatientConfirmationModal";
import useCaptureAndUploadImage from "../../Hooks/useCaptureAndUploadImage";


const AddPatient = () => {

  const storedUserDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails") || "{}");
    } catch (error) {
      console.error("Failed to read userDetails from localStorage", error);
      return {};
    }
  }, []);

  const login = useSelector((state) => state.authSlice.user) || storedUserDetails;
  const token = login?.token || "";
  const [selectedGender, setSelectedGender] = useState("");
  const langData = useSelector((state) => state.langSlice.langData || []);
  const [loading, setLoading] = useState(false);
  const [showAadhaar, setShowAadhaar] = useState(false);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [cameraMode, setCameraMode] = useState(false);
  const [modal, setModal] = useState(false);

  const labNo = login?.CentreID || "";

  // ✅ today's date for max attribute (YYYY-MM-DD)
  const today = new Date();
  const localDate = today.toLocaleDateString("en-CA"); // YYYY-MM-DD

  const [form, setForm] = useState({
    Adhaar: "",
    imageURL: null,
    dob: "",
    EmailId: "",
    Gender: selectedGender,
    Name: "",
    Place: login?.Address || "",
    labNo: labNo,
    contactNumber: "",
    Age: "", // ✅ only for UI, not required by backend
  });

  useEffect(() => {
    if (login?.Address) {
      setForm((prev) => (prev.Place ? prev : { ...prev, Place: login.Address }));
    }
  }, [login]);

  const { setImagePreview, imagePreview, error, captureAndUpload } =
    useCaptureAndUploadImage(token);

  const { validateEmail } = useEmailValidation();

  const handleRadio = (e) => {
    const { value } = e.target;
    setSelectedGender(value);
    setForm((prev) => ({
      ...prev,
      Gender: value,
    }));
  };

  // ✅ ONLY ONE handleChangeEvent (DOB -> Age autofill + future date guard)
  const handleChangeEvent = (event) => {
    event.preventDefault();
    const { name, value } = event.target;

    setForm((prev) => {
      const updated = { ...prev, [name]: value };

      // ✅ If DOB changes, auto-calc Age
      if (name === "dob") {
        if (!value) {
          updated.Age = "";
          return updated;
        }

        // ✅ block future dates (extra guard)
        if (value > localDate) {
          updated.dob = "";
          updated.Age = "";
          return updated;
        }

        const dobDate = new Date(value);
        const todayDate = new Date();

        if (!isNaN(dobDate.getTime())) {
          let age = todayDate.getFullYear() - dobDate.getFullYear();
          const m = todayDate.getMonth() - dobDate.getMonth();

          if (m < 0 || (m === 0 && todayDate.getDate() < dobDate.getDate())) {
            age--;
          }

          updated.Age = age >= 0 ? String(age) : "";
        } else {
          updated.Age = "";
        }
      }

      return updated;
    });
  };

  const modalOpen = async () => {
    const isValidPhoneNumber = (number) => /^\d{10}$/.test(number.trim());
    const isEmptyField = (field) => !field || field.trim() === "";
    const isValidGender = (gender) => ["male", "female", "other"].includes(gender);
    const isValidPlace = (place) => !isEmptyField(place);

    const isValidDate = (dateStr) => {
      const date = new Date(dateStr);
      const today = new Date();
      const matchesFormat = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
      const isNotFutureDate = dateStr <= localDate && date <= today;
      return !isNaN(date) && matchesFormat && isNotFutureDate;
    };

    let isValid = true;

    if (isValid) {
      if (!isValidDate(form.dob)) {
        toast.error(
          translations[langData].dobError || "Invalid date of birth format",
          { position: toast.POSITION.TOP_CENTER }
        );
        isValid = false;
      }

      const isEmail = validateEmail(form.EmailId);
      if (isEmail !== "DELIVERABLE") {
        toast.error(translations[langData].emailError || isEmail, {
          position: toast.POSITION.TOP_CENTER,
        });
        isValid = false;
      }

      if (!isValidGender(form.Gender)) {
        toast.error(
          translations[langData].genderError || "Please select a valid gender",
          { position: toast.POSITION.TOP_CENTER }
        );
        isValid = false;
      }

      if (!isValidPlace(form.Place)) {
        toast.error(translations[langData].placeError || "Place cannot be empty", {
          position: toast.POSITION.TOP_CENTER,
        });
        isValid = false;
      }

      if (!isValidPhoneNumber(form.contactNumber)) {
        toast.error(
          translations[langData].phoneError ||
            "Invalid phone number (must be 10 digits)",
          { position: toast.POSITION.TOP_CENTER }
        );
        isValid = false;
      }
    }

    if (isValid) setModal(true);
    else setLoading(false);
  };

  const modalClose = () => setModal(false);

  const calculateDobFromAge = (age) => {
    const ageNum = parseInt(age, 10);
    if (isNaN(ageNum) || ageNum <= 0) return "";

    const today = new Date();
    const year = today.getFullYear() - ageNum;
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const handleAgeChange = (event) => {
    event.preventDefault();
    const { value } = event.target;

    if (!/^\d*$/.test(value)) return;

    setForm((prev) => {
      const next = { ...prev, Age: value };

      if (value) {
        const dobStr = calculateDobFromAge(value);
        if (dobStr) next.dob = dobStr;
      }

      return next;
    });
  };

  const handleCapturePhoto = async () => {
    try {
      setCameraMode(true);
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const videoElement = document.querySelector("#video");
      videoElement.srcObject = stream;
    } catch (error) {
      console.error("Error accessing the camera:", error);
    }
  };

  const captureImage = () => {
    captureAndUpload({
      videoSelector: "#video",
      setCameraMode: setCameraMode,
      setShowUploadOptions: setShowUploadOptions,
      setForm: setForm,
    });
  };

  const handleChooseFromFile = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
    setShowUploadOptions(false);
  };

  return (
    <div className="bg-[#C7CEE5] min-h-screen h-full">
      <div className=" p-4 sm:p-8 lg:py-10 lg:px-28">
        <div className="bg-white rounded-3xl h-[70%] w-full shadow-lg p-6 lg:p-8 mx-auto z-10 relative">
          <Link
            to="/add-patient"
            className="flex justify-end -mt-8 -mr-8 lg:mt-[-20px] lg:mr-[-20px]"
          >
            <img src={cross} alt="crossicon" />
          </Link>

          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#9669E0] mb-6">
            {translations[langData].addPatient}
          </h1>

          <div className="relative w-[20%] ml-5">
            <div className="flex items-center justify-between pr-1">
              <span className="text-xl font-semibold text-[#5C5CAB99]">
                {translations[langData].showAdhaar}
              </span>

              <label className="inline-flex relative items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={showAadhaar}
                  onChange={() => setShowAadhaar((prev) => !prev)}
                />

                <div className="w-16 h-8 bg-gray-200 rounded-full peer-focus:ring-green-300 transition-colors peer-checked:bg-[#9669E0] shadow-md relative">
                  <span
                    className={`
                      absolute top-[3px]
                      ${showAadhaar ? "left-[30px]" : "left-[10px]"}
                      flex items-center justify-center
                      w-6 h-6
                      transition-transform duration-300 transform
                    `}
                  >
                    <span className="w-full h-full rounded-full overflow-hidden shadow-md bg-white flex items-center justify-center text-[10px] font-semibold text-[#5C5CAB]">
                      {showAadhaar ? "On" : "Off"}
                    </span>
                  </span>
                </div>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 mr-24 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:mt-10">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <img src={patientname} alt="patientnameicon" />
              </span>
              <input
                name="Name"
                required
                type="text"
                value={form.Name}
                onChange={handleChangeEvent}
                placeholder={translations[langData].name}
                className="pl-10 pr-4 p-3 w-80 h-12 rounded-lg bg-[#D7D4F8] outline-none"
              />
            </div>

            <div className="relative ">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <img src={contact} alt="patientnameicon" />
              </span>

              <span className="absolute inset-y-0 left-8 flex items-center text-black ml-1">
                +91
              </span>

              <input
                name="contactNumber"
                type="text"
                required
                value={form.contactNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) handleChangeEvent(e);
                }}
                placeholder={translations[langData].phone}
                className="pl-16 ml-0 pr-4 p-3 w-80 h-12 rounded-lg bg-[#D7D4F8] outline-none"
                maxLength="10"
              />
            </div>

            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <img src={labnumber} alt="patientnameicon" />
              </span>
              <input
                type="text"
                value={labNo || form.labNo}
                onChange={(e) => setForm({ ...form, labNo: e.target.value })}
                placeholder={translations[langData].labNo}
                className="pl-10 pr-4 p-3 w-40 h-12 rounded-lg bg-[#D7D4F8] outline-none"
              />
            </div>

            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <img src={patientage} alt="patientnameicon" />
              </span>

              {/* ✅ No future date selection */}
              <input
                name="dob"
                type="date"
                value={form.dob || ""}
                onChange={handleChangeEvent}
                max={localDate}
                className="pl-10 pr-4 p-3 w-40 h-12 rounded-lg bg-[#D7D4F8] outline-none"
              />

              <input
                name="Age"
                type="text"
                value={form.Age}
                onChange={handleAgeChange}
                placeholder={translations[langData].age || "Age"}
                className=" ml-2 p-6 w-20 h-12 rounded-lg bg-[#D7D4F8] outline-none"
                maxLength="3"
              />
            </div>

            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <img src={emailid} alt="patientnameicon" />
              </span>
              <input
                name="EmailId"
                type="text"
                value={form.EmailId}
                onChange={handleChangeEvent}
                placeholder={translations[langData].email}
                className="pl-10 pr-4 p-3 w-72 h-12 rounded-lg bg-[#D7D4F8] outline-none"
              />
            </div>

            <div className="flex text-sm items-center space-x-2 mt-4">
              <img src={gender} alt="gendericon" />
              <span className="font-bold text-[#5C5CAB99]">Gender</span>

              <label className="space-x-0 cursor-pointer">
                <input
                  type="radio"
                  required
                  onChange={handleRadio}
                  name="Gender"
                  value="male"
                  checked={selectedGender === "male"}
                />
                <span>MALE</span>
              </label>

              <label className="space-x-0 cursor-pointer">
                <input
                  type="radio"
                  required
                  onChange={handleRadio}
                  name="Gender"
                  value="female"
                  checked={selectedGender === "female"}
                />
                <span>FEMALE</span>
              </label>

              <label className="space-x-0 cursor-pointer">
                <input
                  type="radio"
                  required
                  onChange={handleRadio}
                  name="Gender"
                  value="other"
                  checked={selectedGender === "other"}
                />
                <span>OTHER</span>
              </label>
            </div>

            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <img src={place} alt="placeicon" />
              </span>
              <input
                name="Place"
                type="text"
                value={form.Place}
                onChange={(e) => setForm({ ...form, Place: e.target.value })}
                placeholder={translations[langData].place}
                className="pl-10 pr-4 p-3 w-80 h-12 rounded-lg bg-[#D7D4F8] outline-none"
              />
            </div>

            <div className="relative">
              {showAadhaar && (
                <>
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <img src={patientid} alt="patientnameicon" />
                  </span>
                  <input
                    name="Adhaar"
                    type="number"
                    value={form.Adhaar}
                    onChange={handleChangeEvent}
                    placeholder={translations[langData].adhaarNo || ""}
                    maxLength="12"
                    onInput={(e) => {
                      if (e.target.value.length > 12) {
                        e.target.value = e.target.value.slice(0, 12);
                      }
                    }}
                    className="pl-10 pr-4 p-3 w-80 h-12 rounded-lg bg-[#D7D4F8] outline-none"
                  />
                </>
              )}
            </div>
          </div>

          <div className="absolute flex flex-col items-center right-4 lg:right-10 top-4 lg:top-[69px]">
            <div className="w-24 h-24 bg-[#D7D4F8] rounded-full flex border-4 border-[#9669E0] items-center justify-center overflow-hidden">
              <img
                src={imagePreview || user}
                alt="Patient"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex mt-2 items-center space-x-2">
              <img src={imgupload} alt="Upload Icon" />
              <button
                onClick={() => setShowUploadOptions(true)}
                className="text-sm lg:text-base text-[#9669E0] cursor-pointer"
              >
                Upload Patient Photo
              </button>
            </div>
          </div>

          {showUploadOptions && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
              <div className="bg-white rounded-lg p-6 w-96">
                <h3 className="text-lg font-semibold mb-4 text-center">
                  Choose an Option
                </h3>
                <div className="flex flex-col space-y-4">
                  <button
                    onClick={handleCapturePhoto}
                    className="flex items-center justify-center p-4 border rounded-lg bg-gray-100 hover:bg-gray-200"
                  >
                    <span className="text-[#9669E0] font-semibold">
                      Capture Photo
                    </span>
                  </button>

                  <label className="flex items-center justify-center p-4 border rounded-lg cursor-pointer bg-gray-100 hover:bg-gray-200">
                    <span className="text-[#9669E0] font-semibold">
                      Choose from File
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleChooseFromFile}
                      className="hidden"
                    />
                  </label>

                  <button
                    onClick={() => setShowUploadOptions(false)}
                    className="text-sm font-bold bg-red-500 text-white p-2 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {cameraMode && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
              <div className="bg-white rounded-lg p-6 w-full lg:w-96">
                <video id="video" autoPlay className="w-full h-auto rounded-lg" />
                <div className="flex justify-between mt-4">
                  <button
                    onClick={captureImage}
                    className="bg-green-500 text-white px-4 py-2 rounded-lg"
                  >
                    Capture
                  </button>
                  <button
                    onClick={() => setCameraMode(false)}
                    className="bg-red-500 text-white px-4 py-2 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col lg:flex-row justify-between items-center lg:items-start">
            <button
              onClick={modalOpen}
              className="mt-6 lg:mt-32 text-sm lg:text-base font-bold h-12 w-56 px-8 py-3 bg-[#9669E0] text-white rounded-lg flex items-center justify-center space-x-3 lg:space-x-5"
            >
              <p>{translations[langData].addPatient} </p>
              <img src={addpatient} alt="addpatienticon" />
            </button>

            {error ? (
              <div className="">
                <p className="mt-6 lg:mt-32 text-sm lg:text-base font-bold h-12 w-56 px-8 py-3 bg-[red] text-white rounded-lg flex items-center justify-center space-x-3 lg:space-x-5">
                  *{error}
                </p>
              </div>
            ) : null}
          </div>
        </div>

        <Link to="../faq" className="fixed z-10 bottom-8 right-28 ">
          <img src={help} alt="helpicon" className="w-10 lg:w-12" />
        </Link>
      </div>

      <ToastContainer />

      <div className="fixed bottom-0 right-0 z-0 w-full lg:w-auto">
        <img src={sidevector} alt="sidevector" className="w-full lg:w-auto" />
      </div>

      <AddPatientConfirmationModal isOpen={modal} isClose={modalClose} formData={form} />

      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="w-10 h-10 border-4 border-t-red-500 border-gray-300 rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

export default AddPatient;
